import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faCheck, faTimes, faClose, faSearch, faChevronDown, faChevronRight, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useGlobalData } from '../../contexts/GlobalDataContext';
import { ECRCode } from '../../types/ecrCode';
import { Brand } from '../../types/brand';
import { Label } from '../../types/label';

type AllowedCategory = 'ecrCode' | 'brand' | 'labels' | 'contentUnit' | 'unit' | 'baseUnit' | 'baseComparisonUnitCode' | 'unitDescriptor' | 'country' | 'storageZone' | 'packagingUnit' | 'line' | 'markantUnitDescriptor' | 'fangzone' | 'fangmethode' | 'dangerousGoods';

interface UniversalDropdownPopupProps {
  category: AllowedCategory;
  value?: string | string[];
  onChange: (value: string | string[]) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  allowAdminMode?: boolean;
  className?: string;
  isMultiSelect?: boolean;
}

export const UniversalDropdownPopup: React.FC<UniversalDropdownPopupProps> = ({
  category,
  value,
  onChange,
  placeholder = 'Auswählen...',
  label,
  required = false,
  disabled = false,
  allowAdminMode = true,
  className = '',
  isMultiSelect = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [newOptionValue, setNewOptionValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Use GlobalDataContext for all data
  const globalData = useGlobalData();

  const isExternalData = ['ecrCode', 'brand', 'labels'].includes(category);
  
  // Get data from GlobalDataContext
  let loading: boolean;
  let options: any[];
  
  if (isExternalData) {
    loading = globalData.isLoading;
    switch (category) {
      case 'ecrCode':
        options = Array.isArray(globalData.ecrCodes) ? globalData.ecrCodes : [];
        console.log('[UniversalDropdown] ECR Codes:', {
          category,
          loading,
          dataReady: globalData.isReady,
          ecrCodesCount: options.length,
          isArray: Array.isArray(globalData.ecrCodes)
        });
        break;
      case 'brand':
        options = Array.isArray(globalData.brands) ? globalData.brands : [];
        console.log('[UniversalDropdown] Brands:', {
          category,
          loading,
          dataReady: globalData.isReady,
          brandsCount: options.length,
          isArray: Array.isArray(globalData.brands)
        });
        break;
      case 'labels':
        options = Array.isArray(globalData.labels) ? globalData.labels : [];
        console.log('[UniversalDropdown] Labels:', {
          category,
          loading,
          dataReady: globalData.isReady,
          labelsCount: options.length,
          isArray: Array.isArray(globalData.labels)
        });
        break;
      default:
        options = [];
    }
  } else {
    // Use dropdown data from GlobalDataContext for standard dropdown categories
    loading = globalData.isLoading;
    const dropdownOptions = globalData.dropdownData[category as keyof typeof globalData.dropdownData];
    options = Array.isArray(dropdownOptions) ? dropdownOptions : [];
  }

  // Transform line data to match expected format
  if (category === 'line' && Array.isArray(options)) {
    options = options.map(line => ({
      _id: line._id,
      value: line._Value_DE,
      label_DE: line._Value_DE
    }));
  }

  const getDisplayText = () => {
    if (loading) return 'Wird geladen...';
    
    if (isExternalData) {
      if (category === 'labels' && Array.isArray(value)) {
        if (value.length === 0) return placeholder;
        
        // Finde die ausgewählten Labels und zeige ihre Namen an
        const selectedLabels = Array.isArray(options) ? (options as Label[]).filter(label => 
          value.includes(label.value)
        ) : [];
        
        if (selectedLabels.length === 0) return placeholder;
        
        // Zeige bis zu 2 Label-Namen, dann "und X weitere"
        if (selectedLabels.length === 1) {
          return selectedLabels[0].value;
        } else if (selectedLabels.length === 2) {
          return `${selectedLabels[0].value}, ${selectedLabels[1].value}`;
        } else {
          return `${selectedLabels[0].value}, ${selectedLabels[1].value} und ${selectedLabels.length - 2} weitere`;
        }
      }
      
      if (category === 'ecrCode' && value) {
        const findCode = (codes: any[]): any => {
          if (!Array.isArray(codes)) return null;
          for (const code of codes) {
            if (code.fullCode === value || code.value === value) return code;
            if (code.children && Array.isArray(code.children)) {
              const found = findCode(code.children);
              if (found) return found;
            }
          }
          return null;
        };
        const foundCode = findCode(Array.isArray(options) ? options as ECRCode[] : []);
        return foundCode ? `${foundCode.fullCode} - ${foundCode.description}` : value;
      }
      
      if (category === 'brand' && value) {
        const brand = Array.isArray(options) ? (options as Brand[]).find(b => b.name === value || b.value === value) : null;
        return brand ? brand.name : value;
      }
    } else {
      const selectedOption = Array.isArray(options) ? options.find(opt => opt.value === value) : null;
      return selectedOption ? selectedOption.label_DE : (value || placeholder);
    }
    
    return placeholder;
  };

  const getCategoryDisplayName = () => {
    switch (category) {
      case 'ecrCode': return 'ECR Warengruppe auswählen';
      case 'brand': return 'Marke auswählen';
      case 'labels': return 'Labels auswählen';
      case 'unitDescriptor': return 'Einheit auswählen';
      case 'packagingUnit': return 'Verpackung auswählen';
      case 'unit': return 'Einheit auswählen';
      case 'contentUnit': return 'Inhaltseinheit auswählen';
      case 'baseUnit': return 'Grundeinheit auswählen';
      case 'baseComparisonUnitCode': return 'Vergleichseinheit auswählen';
      case 'country': return 'Land auswählen';
      case 'storageZone': return 'Lagerzone auswählen';
      case 'line': return 'Linie auswählen';
      case 'markantUnitDescriptor': return 'Markant Einheit auswählen';
      case 'fangzone': return 'Fangzone auswählen';
      case 'fangmethode': return 'Fangmethode auswählen';
      case 'dangerousGoods': return 'Gefahrgut auswählen';
      default: return 'Option auswählen';
    }
  };

  const handleSelect = (optionValue: string) => {
    if (category === 'labels' && isMultiSelect) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter(v => v !== optionValue)
        : [...currentValues, optionValue];
      onChange(newValues);
    } else {
      onChange(optionValue);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleCreateOption = async () => {
    if (!newOptionLabel.trim() || !newOptionValue.trim()) return;

    try {
      // TODO: Implement create option functionality via GlobalDataContext
      console.log('Create option not implemented yet:', {
        category,
        value: newOptionValue.trim(),
        label: newOptionLabel.trim()
      });
      
      setNewOptionLabel('');
      setNewOptionValue('');
    } catch (error) {
      console.error('Fehler beim Erstellen der Option:', error);
    }
  };

  const renderECRCodeTree = (codes: ECRCode[], level = 0) => {
    console.log('[UniversalDropdown] renderECRCodeTree called:', {
      codesCount: codes?.length || 0,
      level,
      firstCodeSample: codes?.[0] ? {
        value: codes[0].value,
        description: codes[0].description,
        hasChildren: (codes[0].children?.length || 0) > 0,
        codeLevel: codes[0].level
      } : null
    });

    if (!codes || codes.length === 0) {
      console.log('[UniversalDropdown] renderECRCodeTree: No codes to render');
      return null;
    }

    return codes.map((code) => {
      // Nur Level 3 (vollständige ECR-Codes) sind auswählbar
      const isSelectable = code.level === 3;
      const hasChildren = code.children && code.children.length > 0;
      
      return (
        <div key={code.fullCode || code.value} className="space-y-1">
          <div 
            className={`flex items-center p-3 rounded-xl transition-all duration-200 ${
              level > 0 ? `ml-${level * 4}` : ''
            } ${
              isSelectable 
                ? 'hover:bg-blue-50 cursor-pointer' 
                : hasChildren 
                  ? 'hover:bg-gray-50 cursor-default' 
                  : 'cursor-not-allowed opacity-60'
            }`}
            onClick={() => isSelectable ? handleSelect(code.fullCode || code.value) : undefined}
          >
            {hasChildren && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newExpanded = new Set(expandedNodes);
                  if (newExpanded.has(code.fullCode || code.value)) {
                    newExpanded.delete(code.fullCode || code.value);
                  } else {
                    newExpanded.add(code.fullCode || code.value);
                  }
                  setExpandedNodes(newExpanded);
                }}
                className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mr-3 hover:bg-gray-200 transition-colors duration-200"
              >
                <FontAwesomeIcon
                  icon={expandedNodes.has(code.fullCode || code.value) ? faChevronDown : faChevronRight}
                  className="w-3 h-3 text-gray-600"
                />
              </button>
            )}
            <div className="flex-1 min-w-0">
              <div className={`font-mono text-sm font-medium ${
                isSelectable ? 'text-blue-600' : 'text-gray-500'
              }`}>
                {code.fullCode || code.value}
                {!isSelectable && code.level < 3 && (
                  <span className="ml-2 text-xs text-gray-400 font-normal">
                    (Bitte Untergruppe wählen)
                  </span>
                )}
              </div>
              {code.description && (
                <div className={`text-sm truncate ${
                  isSelectable ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  {code.description}
                </div>
              )}
            </div>
            {isSelectable && (
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                <FontAwesomeIcon icon={faCheck} className="w-3 h-3 text-blue-600" />
              </div>
            )}
          </div>
          {hasChildren && expandedNodes.has(code.fullCode || code.value) && (
            <div className="ml-6 space-y-1">
              {renderECRCodeTree(code.children || [], level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  const renderOptions = () => {
    console.log('[UniversalDropdown] renderOptions called:', {
      category,
      isExternalData,
      optionsLength: options?.length || 0,
      searchTerm,
      loading
    });

    if (isExternalData) {
      if (category === 'ecrCode') {
        // Rekursive Funktion um ECR-Codes zu filtern und dabei die Hierarchie zu erhalten
        const filterECRCodesRecursively = (codes: ECRCode[]): ECRCode[] => {
          const filtered: ECRCode[] = [];
          
          for (const code of codes) {
            // Prüfe ob dieser Code oder seine Kinder den Suchterm enthalten
            const matchesSearch = !searchTerm || 
              code.fullCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              code.description?.toLowerCase().includes(searchTerm.toLowerCase());
            
            // Filtere Kinder rekursiv
            const filteredChildren = code.children ? filterECRCodesRecursively(code.children) : [];
            
            // Zeige den Code wenn er selbst matches oder gefilterte Kinder hat
            if (matchesSearch || filteredChildren.length > 0) {
              filtered.push({
                ...code,
                children: filteredChildren.length > 0 ? filteredChildren : code.children
              });
            }
          }
          
          return filtered;
        };
        
        const filteredCodes = searchTerm 
          ? filterECRCodesRecursively(options as ECRCode[])
          : (options as ECRCode[]);
        
        console.log('[UniversalDropdown] ECR Codes filtered:', {
          originalCount: options.length,
          filteredCount: filteredCodes.length,
          searchTerm
        });
        
        if (filteredCodes.length === 0) {
          return (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faSearch} className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-gray-500 text-sm">Keine ECR-Codes gefunden</p>
              <p className="text-gray-400 text-xs mt-1">
                Hinweis: Nur die dritte Ebene (vollständige Codes) ist auswählbar
              </p>
            </div>
          );
        }
        
        return (
          <div className="space-y-1">
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
              <p className="text-blue-800 text-xs font-medium flex items-center">
                <FontAwesomeIcon icon={faInfoCircle} className="w-3 h-3 mr-2" />
                Nur die dritte Ebene (vollständige ECR-Codes) kann ausgewählt werden
              </p>
            </div>
            {renderECRCodeTree(filteredCodes)}
          </div>
        );
      }
      
      if (category === 'brand') {
        const filteredBrands = searchTerm
          ? (options as Brand[]).filter(brand =>
              brand.name?.toLowerCase().includes(searchTerm.toLowerCase())
            )
          : (options as Brand[]);
          
        console.log('[UniversalDropdown] Brands filtered:', {
          originalCount: options.length,
          filteredCount: filteredBrands.length,
          searchTerm
        });
          
        return (
          <div className="space-y-1">
            {filteredBrands.map((brand) => (
              <button
                key={brand._id || brand.name}
                onClick={() => handleSelect(brand.name || brand.value)}
                className="w-full flex items-center p-3 rounded-xl hover:bg-blue-50 cursor-pointer transition-all duration-200 text-left"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center mr-3">
                  <span className="text-white font-semibold text-sm">
                    {brand.name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <span className="text-gray-900 font-medium">{brand.name}</span>
              </button>
            ))}
          </div>
        );
      }
      
      if (category === 'labels') {
        const selectedLabels = Array.isArray(value) ? value : [];
        const filteredLabels = searchTerm
          ? (options as Label[]).filter(label =>
              label.value?.toLowerCase().includes(searchTerm.toLowerCase())
            )
          : (options as Label[]);
          
        console.log('[UniversalDropdown] Labels filtered:', {
          originalCount: options.length,
          filteredCount: filteredLabels.length,
          searchTerm,
          selectedLabels: selectedLabels.length
        });
          
        return (
          <div className="space-y-1">
            {filteredLabels.map((label) => (
              <button
                key={label._id || label.value}
                onClick={() => handleSelect(label.value)}
                className="w-full flex items-center p-3 rounded-xl hover:bg-blue-50 cursor-pointer transition-all duration-200 text-left"
              >
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mr-3 transition-all duration-200 ${
                  selectedLabels.includes(label.value) 
                    ? 'bg-blue-500 border-blue-500' 
                    : 'border-gray-300'
                }`}>
                  {selectedLabels.includes(label.value) && (
                    <FontAwesomeIcon icon={faCheck} className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="text-gray-900">{label.value}</span>
              </button>
            ))}
          </div>
        );
      }
    } else {
      const filteredOptions = searchTerm
        ? options.filter(opt =>
            opt.label_DE?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            opt.value?.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : options;

      return (
        <div className="space-y-1">
          {filteredOptions.map((option) => (
            <button
              key={option._id || option.value}
              onClick={() => handleSelect(option.value)}
              className={`w-full flex items-center p-3 rounded-xl cursor-pointer transition-all duration-200 text-left ${
                value === option.value ? 'bg-blue-50 text-blue-900' : 'hover:bg-gray-50 text-gray-900'
              }`}
            >
              <span className="font-medium">{option.label_DE}</span>
            </button>
          ))}
        </div>
      );
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Data is now loaded via GlobalDataContext
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]); // GlobalDataContext handles all data loading

  return (
    <>
      {label && (
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className={`relative ${className}`}>
        <button
          type="button"
          onClick={() => !disabled && !loading && setIsOpen(true)}
          disabled={disabled || loading}
          className={`
            w-full h-[36px] px-4 py-1.5 text-left border border-gray-300 rounded-xl text-sm transition-all duration-200
            focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            ${disabled || loading ? 'bg-gray-50 cursor-not-allowed' : 'bg-white hover:border-gray-400 hover:shadow-sm cursor-pointer'}
            flex items-center justify-between
          `}
        >
          <span className={`${!value || (Array.isArray(value) && value.length === 0) ? 'text-gray-500' : 'text-gray-900'} truncate text-sm`}>
            {getDisplayText()}
          </span>
          <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
            <FontAwesomeIcon 
              icon={faChevronDown} 
              className="w-2.5 h-2.5 text-gray-500"
            />
          </div>
        </button>
      </div>

      {/* Modern Popup Modal */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsOpen(false);
              setIsAdminMode(false);
              setSearchTerm('');
            }
          }}
        >
          <div 
            className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[70vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50">
              <h3 className="text-lg font-semibold text-gray-900">
                {getCategoryDisplayName()}
              </h3>
              <div className="flex items-center gap-2">
                {allowAdminMode && !isExternalData && (
                  <button
                    onClick={() => setIsAdminMode(!isAdminMode)}
                    className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 ${
                      isAdminMode ? 'bg-blue-500 text-white shadow-md' : 'bg-white text-gray-500 hover:bg-gray-50 shadow-sm border border-gray-200'
                    }`}
                  >
                    <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => {
                    setIsOpen(false);
                    setIsAdminMode(false);
                    setSearchTerm('');
                  }}
                  className="w-9 h-9 bg-white text-gray-500 rounded-full flex items-center justify-center hover:bg-gray-50 transition-all duration-200 shadow-sm border border-gray-200"
                >
                  <FontAwesomeIcon icon={faClose} className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="px-6 py-4">
              <div className="relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faSearch} className="w-3 h-3 text-gray-500" />
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="Suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-14 pr-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 transition-all duration-200 text-sm"
                />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : options.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FontAwesomeIcon icon={faSearch} className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-sm">
                    {searchTerm ? 'Keine Ergebnisse gefunden' : 'Keine Optionen verfügbar'}
                  </p>
                </div>
              ) : (
                <div className="pb-4">
                  {renderOptions()}
                </div>
              )}
            </div>

            {/* Admin Footer */}
            {isAdminMode && !isExternalData && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      placeholder="Anzeigename"
                      value={newOptionLabel}
                      onChange={(e) => setNewOptionLabel(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Wert"
                      value={newOptionValue}
                      onChange={(e) => setNewOptionValue(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <button
                    onClick={handleCreateOption}
                    disabled={!newOptionLabel.trim() || !newOptionValue.trim()}
                    className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 font-medium text-sm"
                  >
                    <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
                    Neue Option hinzufügen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};